import {isFeatureEnabled} from '@github-ui/feature-flags'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {RepositoryLabelsInternal} from '@github-ui/repository-label/RepositoryLabels'
import {useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {getRepoIssuesSidebarPanes} from '../components/sidebar/get-repo-issues-sidebar-panes'
import {useQueryContext} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {IssueIndexPage} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {RepositoryLabelIndexPageContentInternal$key} from './__generated__/RepositoryLabelIndexPageContentInternal.graphql'
import type {RepositoryLabelIndexPageQuery} from './__generated__/RepositoryLabelIndexPageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryLabelIndexPageQuery(
    $name: String!
    $owner: String!
    $first: Int!
    $orderField: LabelOrderField = NAME
    $orderDirection: OrderDirection = ASC
    $skip: Int!
    $query: String
  ) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...RepositoryLabelIndexPageContentInternal
        @arguments(first: $first, orderField: $orderField, orderDirection: $orderDirection, skip: $skip, query: $query)
    }
  }
`

export const RepositoryLabelIndexPage: EntryPointComponent<
  {pageQuery: RepositoryLabelIndexPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null
  return (
    <AnalyticsWrapper category="Repository Label Index">
      <RepositoryLabelIndexContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryLabelIndexContent({pageQueryRef}: {pageQueryRef: PreloadedQuery<RepositoryLabelIndexPageQuery>}) {
  const pageData = usePreloadedQuery<RepositoryLabelIndexPageQuery>(PageQuery, pageQueryRef)

  const {setCurrentViewId} = useQueryContext()

  useEffect(() => {
    // When issue_pinned_views is on, the sidebar hook preloads IssueIndexPage.
    // Remove this block when the flag is cleaned up.
    if (!isFeatureEnabled('issue_pinned_views')) {
      return preloadWhenIdle(() => {
        IssueIndexPage.preload()
      })
    }
  }, [])

  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryLabelIndexPageContentInternal repository={okResult.value} />
}

function RepositoryLabelIndexPageContentInternal({
  repository,
}: {
  repository: RepositoryLabelIndexPageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryLabelIndexPageContentInternal on Repository
      @argumentDefinitions(
        first: {type: "Int!"}
        orderField: {type: "LabelOrderField", defaultValue: NAME}
        orderDirection: {type: "OrderDirection", defaultValue: ASC}
        skip: {type: "Int!"}
        query: {type: "String"}
      ) {
        ...RepositoryLabelsInternal
          @arguments(
            first: $first
            orderField: $orderField
            orderDirection: $orderDirection
            skip: $skip
            query: $query
          )
      }
    `,
    repository,
  )

  const sidebarPanes = getRepoIssuesSidebarPanes()
  const issuePinnedViewsEnabled = isFeatureEnabled('issue_pinned_views')
  if (issuePinnedViewsEnabled) {
    return (
      <RepositoryLabelsInternal
        repository={data}
        leftPane={sidebarPanes.leftPane}
        collapsibleLeftPane={sidebarPanes.collapsibleLeftPane}
      />
    )
  }

  return <RepositoryLabelsInternal repository={data} />
}
